import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { crmFetch } from './crmApi';
import { DEAL_STAGES, DEAL_SOURCES, DEAL_PLANS, computeDealMoney, fmtMoney } from './constants';

const EMPTY = {
  name: '', companyName: '', contactName: '', contactEmail: '', contactPhone: '',
  stage: 'prospect', plan: '', seats: '', mrr: '', setupFee: '', currency: 'EUR',
  source: '', expectedCloseDate: '', lostReason: '', notes: '',
};

export const DealModal = ({ open, onOpenChange, onSaved, deal, defaultStage }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!deal;

  useEffect(() => {
    if (!open) return;
    if (deal) {
      setForm({
        name: deal.name || '', companyName: deal.company_name || '',
        contactName: deal.contact_name || '', contactEmail: deal.contact_email || '', contactPhone: deal.contact_phone || '',
        stage: deal.stage || 'prospect', plan: deal.plan || '', seats: deal.seats ?? '',
        mrr: deal.mrr ?? '', setupFee: deal.setup_fee ?? '', currency: deal.currency || 'EUR',
        source: deal.source || '', expectedCloseDate: deal.expected_close_date || '',
        lostReason: deal.lost_reason || '', notes: deal.notes || '',
      });
      return;
    }
    setForm({ ...EMPTY, stage: defaultStage || 'prospect' });
  }, [open, deal, defaultStage]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const money = computeDealMoney(form);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Deal name is required'); return; }
    if (!form.companyName.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, companyName: form.companyName,
        contactName: form.contactName, contactEmail: form.contactEmail, contactPhone: form.contactPhone,
        stage: form.stage, plan: form.plan || null, seats: form.seats, mrr: form.mrr, setupFee: form.setupFee,
        currency: form.currency, source: form.source || null, expectedCloseDate: form.expectedCloseDate || null,
        lostReason: form.lostReason, notes: form.notes,
      };
      const res = isEdit
        ? await crmFetch(`/deals/${deal.id}`, { method: 'PATCH', body: JSON.stringify(payload) })
        : await crmFetch('/deals', { method: 'POST', body: JSON.stringify(payload) });
      if (!res.success) { toast.error(res.message || 'Failed to save deal'); return; }
      toast.success(isEdit ? 'Deal updated' : 'Deal added 🚀');
      onOpenChange(false);
      onSaved && onSaved(res.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="deal-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Deal' : 'New Software Deal'}</DialogTitle>
          <DialogDescription>A JobastiIQ software opportunity — kept separate from recruitment accounts.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block">Deal Name *</Label>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Platform rollout" data-testid="deal-name-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Company *</Label>
              <Input value={form.companyName} onChange={(e) => set('companyName', e.target.value)} placeholder="e.g. Talentbrücke GmbH" data-testid="deal-company-input" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="mb-1.5 block">Contact Name</Label>
              <Input value={form.contactName} onChange={(e) => set('contactName', e.target.value)} data-testid="deal-contact-name-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Contact Email</Label>
              <Input type="email" value={form.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} data-testid="deal-contact-email-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Contact Phone</Label>
              <Input value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} data-testid="deal-contact-phone-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="mb-1.5 block">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => set('stage', v)}>
                <SelectTrigger data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DEAL_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Plan</Label>
              <Select value={form.plan || 'none'} onValueChange={(v) => set('plan', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="deal-plan-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {DEAL_PLANS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Seats</Label>
              <Input type="number" value={form.seats} onChange={(e) => set('seats', e.target.value)} data-testid="deal-seats-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Source</Label>
              <Select value={form.source || 'none'} onValueChange={(v) => set('source', v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="deal-source-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {DEAL_SOURCES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label className="mb-1.5 block">MRR</Label>
              <Input type="number" value={form.mrr} onChange={(e) => set('mrr', e.target.value)} placeholder="e.g. 1450" data-testid="deal-mrr-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Setup Fee</Label>
              <Input type="number" value={form.setupFee} onChange={(e) => set('setupFee', e.target.value)} data-testid="deal-setup-fee-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Currency</Label>
              <Input value={form.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} maxLength={3} data-testid="deal-currency-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Expected Close</Label>
              <Input type="date" value={form.expectedCloseDate} onChange={(e) => set('expectedCloseDate', e.target.value)} data-testid="deal-close-date-input" />
            </div>
          </div>

          {form.stage === 'lost' && (
            <div>
              <Label className="mb-1.5 block">Lost Reason</Label>
              <Input value={form.lostReason} onChange={(e) => set('lostReason', e.target.value)} placeholder="Why did we lose this deal?" data-testid="deal-lost-reason-input" />
            </div>
          )}

          <div>
            <Label className="mb-1.5 block">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="deal-notes-input" />
          </div>

          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm" data-testid="deal-preview">
            <span className="text-blue-800">ARR: <strong>{fmtMoney(money.arr, form.currency || 'EUR') || '—'}</strong></span>
            <span className="text-blue-400 mx-2">·</span>
            <span className="text-blue-800">TCV (yr 1): <strong>{fmtMoney(money.tcv, form.currency || 'EUR') || '—'}</strong></span>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="deal-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="deal-save-btn">
              {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Deal')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
